import React, { useEffect, useState } from "react";
import {
  Table,
  Pagination,
  Select,
  Skeleton,
  Empty,
  Button,
  Space,
  Tabs,
  DatePicker,
  Tag,
  Popconfirm,
  Modal,
  Descriptions,
  Divider,
  Typography,
  Upload,
  Input,
} from "antd";
import Column from "antd/es/table/Column";
import {
  ReloadOutlined,
  UploadOutlined,
  EditOutlined,
  InboxOutlined,
  FileExcelOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import * as XLSX from "xlsx";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useAxiosInstance from "../../lib/useAxiosInstance";

const { RangePicker } = DatePicker;
const { Paragraph, Text } = Typography;

// Quill toolbar config for editing the news message.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    ["link"],
    ["clean"],
  ],
};

// Strip HTML tags to render a short plain-text preview in the table.
const stripHtml = (html) => {
  if (!html) return "";
  if (typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  }
  return String(html).replace(/<[^>]*>/g, "");
};

// ─── Summary stat card ────────────────────────────────────────────────────────
const StatCard = ({ label, value, accent }) => (
  <div
    style={{
      flex: 1,
      minWidth: 150,
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderLeft: `4px solid ${accent}`,
      borderRadius: 12,
      padding: "14px 18px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}
  >
    <div style={{ fontSize: 26, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
      {value ?? 0}
    </div>
    <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, fontWeight: 600 }}>
      {label}
    </div>
  </div>
);

// ─── Status badge helper ──────────────────────────────────────────────────────
const StatusTag = ({ status }) => {
  const config = {
    approved: { color: "green", label: "Approved" },
    rejected: { color: "red", label: "Rejected" },
    pending: { color: "orange", label: "Pending" },
  };
  const { color, label } = config[status] || { color: "default", label: status };
  return <Tag color={color}>{label}</Tag>;
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const ReportDetailModal = ({
  record,
  activeTab,
  onClose,
  onStatusChange,
  updating,
  categoryOptions = [],
  onSaveEdit,
  saving,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [editCategory, setEditCategory] = useState("");

  // Seed edit fields whenever a new record is opened.
  useEffect(() => {
    if (record) {
      setIsEditing(false);
      setEditMessage(record.message || "");
      setEditCategory(record.category || "");
    }
  }, [record?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!record) return null;

  const stringer = record.stringerId || {};

  const handleSave = async () => {
    if (!stripHtml(editMessage).trim()) {
      toast.error("Message cannot be empty");
      return;
    }
    const ok = await onSaveEdit(record, {
      message: editMessage,
      category: editCategory,
    });
    if (ok) setIsEditing(false);
  };

  const actionButtons = () => {
    if (activeTab === "pending") {
      return (
        <Space>
          <Popconfirm
            title="Approve this news report?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => onStatusChange(record, "approved")}
          >
            <Button type="primary" loading={updating} style={{ background: "#16a34a", borderColor: "#16a34a" }}>
              ✓ Approve
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Reject this news report?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => onStatusChange(record, "rejected")}
          >
            <Button danger loading={updating}>
              ✗ Reject
            </Button>
          </Popconfirm>
        </Space>
      );
    }

    if (activeTab === "approved") {
      return (
        <Space>
          <Popconfirm
            title="Move back to pending?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => onStatusChange(record, "pending")}
          >
            <Button loading={updating}>
              ↩ Move to Pending
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Reject this approved report?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => onStatusChange(record, "rejected")}
          >
            <Button danger loading={updating}>
              ✗ Reject
            </Button>
          </Popconfirm>
        </Space>
      );
    }

    // "all" tab — show all three actions based on current status
    if (record.status !== "approved") {
      return (
        <Space>
          <Popconfirm
            title="Approve this news report?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => onStatusChange(record, "approved")}
          >
            <Button type="primary" loading={updating} style={{ background: "#16a34a", borderColor: "#16a34a" }}>
              ✓ Approve
            </Button>
          </Popconfirm>
          {record.status !== "rejected" && (
            <Popconfirm
              title="Reject this news report?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => onStatusChange(record, "rejected")}
            >
              <Button danger loading={updating}>
                ✗ Reject
              </Button>
            </Popconfirm>
          )}
          {record.status === "rejected" && (
            <Popconfirm
              title="Move back to pending?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => onStatusChange(record, "pending")}
            >
              <Button loading={updating}>↩ Pending</Button>
            </Popconfirm>
          )}
        </Space>
      );
    }

    return (
      <Space>
        <Popconfirm
          title="Move back to pending?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => onStatusChange(record, "pending")}
        >
          <Button loading={updating}>↩ Move to Pending</Button>
        </Popconfirm>
        <Popconfirm
          title="Reject this approved report?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => onStatusChange(record, "rejected")}
        >
          <Button danger loading={updating}>
            ✗ Reject
          </Button>
        </Popconfirm>
      </Space>
    );
  };

  return (
    <Modal
      open={!!record}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>News Report Details</span>
          <StatusTag status={record.status} />
        </div>
      }
      onCancel={onClose}
      footer={null}
      width={680}
      destroyOnClose
    >
      {/* Edit toggle */}
      {!isEditing && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        </div>
      )}

      {/* Reporter info */}
      <Descriptions
        column={2}
        size="small"
        bordered
        style={{ marginBottom: 16 }}
        labelStyle={{ fontWeight: 600, background: "#fafafa", width: 140 }}
      >
        <Descriptions.Item label="Reporter" span={1}>
          {record.reporterName || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Phone" span={1}>
          {record.phone || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="District" span={1}>
          {record.district || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Constituency" span={1}>
          {record.constituency || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Category" span={2}>
          {isEditing ? (
            <Select
              allowClear
              showSearch
              placeholder="Select or type a category"
              style={{ width: "100%", maxWidth: 320 }}
              value={editCategory || undefined}
              onChange={(v) => setEditCategory(v || "")}
              onSearch={(v) => setEditCategory(v)}
              options={categoryOptions.map((c) => ({ value: c, label: c }))}
              notFoundContent={null}
            />
          ) : (
            record.category || "—"
          )}
        </Descriptions.Item>
        {stringer?._id && (
          <>
            <Descriptions.Item label="Stringer Name" span={1}>
              {stringer.name || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Stringer District" span={1}>
              {stringer.district || "—"}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Received" span={2}>
          {record.createdAt ? new Date(record.createdAt).toLocaleString() : "—"}
        </Descriptions.Item>
      </Descriptions>

      {/* Message */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          padding: "14px 16px",
          marginBottom: 20,
        }}
      >
        <Text strong style={{ display: "block", marginBottom: 6, color: "#374151" }}>
          Message
        </Text>
        {isEditing ? (
          <ReactQuill
            theme="snow"
            value={editMessage}
            onChange={setEditMessage}
            modules={QUILL_MODULES}
            style={{ background: "#fff" }}
          />
        ) : (
          <div
            style={{
              margin: 0,
              color: "#1f2937",
              fontSize: 14,
              lineHeight: 1.7,
            }}
            className="news-report-message"
            dangerouslySetInnerHTML={{ __html: record.message || "—" }}
          />
        )}
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {/* Action buttons */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {isEditing ? (
          <Space>
            <Button onClick={() => setIsEditing(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save Changes
            </Button>
          </Space>
        ) : (
          actionButtons()
        )}
      </div>
    </Modal>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const NewsReportsList = () => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("pending");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Filters
  const [district, setDistrict] = useState("");
  const [constituency, setConstituency] = useState("");
  const [category, setCategory] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [customRange, setCustomRange] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the message search so we don't hit the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Modal state
  const [selectedReport, setSelectedReport] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Bulk upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  // ── Filter options ──────────────────────────────────────────────────────────
  const filtersQuery = useQuery(
    ["news-report-filters"],
    async () => {
      const res = await axiosInstance.get(`api/news/reports/filters`);
      return res?.data?.data?.data || {};
    },
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const filterOptions = filtersQuery?.data || {};
  const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

  // ── Build query params ──────────────────────────────────────────────────────
  const buildParams = () => {
    const params = new URLSearchParams();
    params.set("page", currentPage);
    params.set("limit", limit);
    params.set("tab", activeTab);
    if (district) params.set("district", district);
    if (constituency) params.set("constituency", constituency);
    if (category) params.set("category", category);
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (dateRange && dateRange !== "custom") params.set("dateRange", dateRange);
    if (dateRange === "custom" && customRange?.length === 2) {
      params.set("dateRange", "custom");
      params.set("startDate", customRange[0].toISOString());
      params.set("endDate", customRange[1].toISOString());
    }
    return params.toString();
  };

  // Location/category/date/search filter params (no page/limit/tab) — shared by stats & export.
  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (district) params.set("district", district);
    if (constituency) params.set("constituency", constituency);
    if (category) params.set("category", category);
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (dateRange && dateRange !== "custom") params.set("dateRange", dateRange);
    if (dateRange === "custom" && customRange?.length === 2) {
      params.set("dateRange", "custom");
      params.set("startDate", customRange[0].toISOString());
      params.set("endDate", customRange[1].toISOString());
    }
    return params;
  };

  // ── Stats (top cards + day-wise summary) ────────────────────────────────────
  const statsQuery = useQuery(
    ["news-report-stats", activeTab, district, constituency, category, dateRange, customRange, debouncedSearch],
    async () => {
      const params = buildFilterParams();
      params.set("tab", activeTab);
      const res = await axiosInstance.get(`api/news/reports/stats?${params.toString()}`);
      return res?.data?.data?.data || res?.data?.data || {};
    },
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const stats = statsQuery?.data || {};

  // Day-wise summary is always scoped to the last 7 days, independent of the
  // date filter applied to the main list below — the main list can show all data.
  const daySummaryQuery = useQuery(
    ["news-report-day-summary", activeTab, district, constituency, category, debouncedSearch],
    async () => {
      const params = new URLSearchParams();
      if (district) params.set("district", district);
      if (constituency) params.set("constituency", constituency);
      if (category) params.set("category", category);
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("dateRange", "last7");
      params.set("tab", activeTab);
      const res = await axiosInstance.get(`api/news/reports/stats?${params.toString()}`);
      const data = res?.data?.data?.data || res?.data?.data || {};
      return data?.daySummary || [];
    },
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const daySummary = daySummaryQuery?.data || [];

  // ── Reports query ───────────────────────────────────────────────────────────
  const reportsQuery = useQuery(
    [
      "news-reports-list",
      activeTab,
      currentPage,
      limit,
      district,
      constituency,
      category,
      dateRange,
      customRange,
      debouncedSearch,
    ],
    async () => {
      const res = await axiosInstance.get(`api/news/reports?${buildParams()}`);
      return res?.data?.data || {};
    },
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const isLoading = reportsQuery?.isFetching || reportsQuery?.isLoading;
  const rows = reportsQuery?.data?.data || [];
  const totalPages = reportsQuery?.data?.totalPages || 0;

  // ── Status change handler ───────────────────────────────────────────────────
  const updateStatus = async (record, status) => {
    try {
      setUpdating(true);
      await axiosInstance.patch(`api/news/reports/${record._id}/status`, { status });
      const labels = { approved: "approved", rejected: "rejected", pending: "moved to pending" };
      toast.success(`News report ${labels[status] || status}`);
      setSelectedReport(null);
      queryClient.invalidateQueries("news-reports-list");
      queryClient.invalidateQueries("news-report-stats");
      queryClient.invalidateQueries("news-report-day-summary");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  // ── Filters / tab helpers ───────────────────────────────────────────────────
  const resetFilters = () => {
    setDistrict("");
    setConstituency("");
    setCategory("");
    setDateRange("");
    setCustomRange([]);
    setSearch("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  const onTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
  };

  // ── Refresh handler ─────────────────────────────────────────────────────────
  const handleRefresh = () => {
    queryClient.invalidateQueries("news-reports-list");
    queryClient.invalidateQueries("news-report-filters");
    queryClient.invalidateQueries("news-report-stats");
    queryClient.invalidateQueries("news-report-day-summary");
    reportsQuery.refetch();
    filtersQuery.refetch();
    statsQuery.refetch();
    daySummaryQuery.refetch();
  };

  // ── Export (approved only, respecting current filters) ───────────────────────
  const describeFilters = () => {
    const parts = [];
    const fmt = (m) => (m?.format ? m.format("DD MMM YYYY") : "");
    if (dateRange === "today") {
      parts.push(`Today (${new Date().toLocaleDateString("en-IN")})`);
    } else if (dateRange === "last7") {
      parts.push("Last 7 days");
    } else if (dateRange === "lastMonth") {
      parts.push("Last month");
    } else if (dateRange === "custom" && customRange?.length === 2) {
      parts.push(`From ${fmt(customRange[0])} to ${fmt(customRange[1])}`);
    }
    if (district) parts.push(`District: ${district}`);
    if (constituency) parts.push(`Constituency: ${constituency}`);
    if (category) parts.push(`Category: ${category}`);
    if (debouncedSearch) parts.push(`Search: "${debouncedSearch}"`);
    return parts;
  };

  const exportToExcel = async () => {
    try {
      setExporting(true);
      // Always export approved records, honouring the other filters.
      const params = buildFilterParams();
      params.set("tab", "approved");
      params.set("page", "1");
      params.set("limit", "100000");
      const res = await axiosInstance.get(`api/news/reports?${params.toString()}`);
      const records = res?.data?.data?.data || [];

      if (!records.length) {
        toast.info("No approved records to export for the selected filters");
        return;
      }

      const filterParts = describeFilters();
      const heading = `Approved News Reports${
        filterParts.length ? " — " + filterParts.join(" · ") : " — All records"
      }`;
      const subHeading = `Generated on ${new Date().toLocaleString("en-IN")} · ${records.length} record(s)`;

      const aoa = [
        [heading],
        [subHeading],
        [],
        ["District", "Constituency", "Category", "Message", "Received"],
        ...records.map((r) => [
          r.district || "",
          r.constituency || "",
          r.category || "",
          stripHtml(r.message),
          r.createdAt ? new Date(r.createdAt).toLocaleString("en-IN") : "",
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 70 },
        { wch: 22 },
      ];
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Approved News");
      XLSX.writeFile(wb, `news-reports-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Exported ${records.length} approved record(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // ── Edit (message / category) handler ────────────────────────────────────────
  const saveReportEdit = async (record, payload) => {
    try {
      setSavingEdit(true);
      const res = await axiosInstance.patch(`api/news/reports/${record._id}`, payload);
      toast.success("News report updated");
      // Reflect the change in the open modal immediately.
      setSelectedReport((prev) =>
        prev && prev._id === record._id ? { ...prev, ...payload } : prev
      );
      queryClient.invalidateQueries("news-reports-list");
      queryClient.invalidateQueries("news-report-filters");
      queryClient.invalidateQueries("news-report-stats");
      queryClient.invalidateQueries("news-report-day-summary");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update report");
      return false;
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Bulk upload handler ──────────────────────────────────────────────────────
  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error("Please choose an Excel file");
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", uploadFile);
      const res = await axiosInstance.post("api/news/reports/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res?.data?.message || "News reports uploaded");
      setUploadOpen(false);
      setUploadFile(null);
      queryClient.invalidateQueries("news-reports-list");
      queryClient.invalidateQueries("news-report-filters");
      queryClient.invalidateQueries("news-report-stats");
      queryClient.invalidateQueries("news-report-day-summary");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // ── Inline row action buttons (compact) ────────────────────────────────────
  const renderRowActions = (record) => {
    if (activeTab === "pending") {
      return (
        <Space size={4}>
          <Popconfirm
            title="Approve?"
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => { e.stopPropagation(); updateStatus(record, "approved"); }}
          >
            <Button
              size="small"
              type="primary"
              style={{ background: "#16a34a", borderColor: "#16a34a", fontSize: 12 }}
              onClick={(e) => e.stopPropagation()}
            >
              Approve
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Reject?"
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => { e.stopPropagation(); updateStatus(record, "rejected"); }}
          >
            <Button
              size="small"
              danger
              onClick={(e) => e.stopPropagation()}
            >
              Reject
            </Button>
          </Popconfirm>
        </Space>
      );
    }

    if (activeTab === "approved") {
      return (
        <Space size={4}>
          <Popconfirm
            title="Move back to pending?"
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => { e.stopPropagation(); updateStatus(record, "pending"); }}
          >
            <Button
              size="small"
              onClick={(e) => e.stopPropagation()}
            >
              Pending
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Reject?"
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => { e.stopPropagation(); updateStatus(record, "rejected"); }}
          >
            <Button
              size="small"
              danger
              onClick={(e) => e.stopPropagation()}
            >
              Reject
            </Button>
          </Popconfirm>
        </Space>
      );
    }

    // "all" tab
    return (
      <Space size={4}>
        {record.status !== "approved" && (
          <Popconfirm
            title="Approve?"
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => { e.stopPropagation(); updateStatus(record, "approved"); }}
          >
            <Button
              size="small"
              type="primary"
              style={{ background: "#16a34a", borderColor: "#16a34a", fontSize: 12 }}
              onClick={(e) => e.stopPropagation()}
            >
              Approve
            </Button>
          </Popconfirm>
        )}
        {record.status === "approved" && (
          <Popconfirm
            title="Move back to pending?"
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => { e.stopPropagation(); updateStatus(record, "pending"); }}
          >
            <Button size="small" onClick={(e) => e.stopPropagation()}>
              Pending
            </Button>
          </Popconfirm>
        )}
        {record.status !== "rejected" && (
          <Popconfirm
            title="Reject?"
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => { e.stopPropagation(); updateStatus(record, "rejected"); }}
          >
            <Button
              size="small"
              danger
              onClick={(e) => e.stopPropagation()}
            >
              Reject
            </Button>
          </Popconfirm>
        )}
        {record.status === "rejected" && (
          <Popconfirm
            title="Move back to pending?"
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => { e.stopPropagation(); updateStatus(record, "pending"); }}
          >
            <Button size="small" onClick={(e) => e.stopPropagation()}>
              ↩ Pending
            </Button>
          </Popconfirm>
        )}
      </Space>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Summary cards */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 18, padding: "0 8px" }}>
        <StatCard label="Total Entries" value={stats.total} accent="#0b65c2" />
        <StatCard label="Approved News" value={stats.approved} accent="#16a34a" />
        <StatCard label="Pending News" value={stats.pending} accent="#f59e0b" />
        <StatCard label="Rejected News" value={stats.rejected} accent="#dc2626" />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={[
          { key: "pending", label: "Pending" },
          { key: "approved", label: "Approved" },
          { key: "all", label: "All" },
        ]}
        tabBarExtraContent={{
          right: (
            <Space>
              <Button
                icon={<FileExcelOutlined />}
                onClick={exportToExcel}
                loading={exporting}
                style={{ background: "#16a34a", borderColor: "#16a34a", color: "#fff" }}
              >
                Export Approved
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setUploadOpen(true)}
              >
                Upload News Report
              </Button>
              <Button
                icon={<ReloadOutlined spin={isLoading} />}
                onClick={handleRefresh}
                loading={isLoading}
              >
                Refresh
              </Button>
            </Space>
          ),
        }}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4 px-2">
        <Input
          allowClear
          placeholder="Search by message..."
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          style={{ width: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          showSearch
          placeholder="District"
          style={{ width: 180 }}
          value={district || undefined}
          onChange={(v) => { setDistrict(v || ""); setCurrentPage(1); }}
          options={toOptions(filterOptions.districts)}
        />
        <Select
          allowClear
          showSearch
          placeholder="Constituency"
          style={{ width: 180 }}
          value={constituency || undefined}
          onChange={(v) => { setConstituency(v || ""); setCurrentPage(1); }}
          options={toOptions(filterOptions.constituencies)}
        />
        <Select
          allowClear
          showSearch
          placeholder="Category"
          style={{ width: 180 }}
          value={category || undefined}
          onChange={(v) => { setCategory(v || ""); setCurrentPage(1); }}
          options={toOptions(filterOptions.categories)}
        />
        <Select
          allowClear
          placeholder="Date"
          style={{ width: 160 }}
          value={dateRange || undefined}
          onChange={(v) => {
            setDateRange(v || "");
            if (v !== "custom") setCustomRange([]);
            setCurrentPage(1);
          }}
          options={[
            { value: "today", label: "Today" },
            { value: "last7", label: "Last 7 days" },
            { value: "lastMonth", label: "Last month" },
            { value: "custom", label: "Custom range" },
          ]}
        />
        {dateRange === "custom" && (
          <RangePicker
            value={customRange}
            onChange={(vals) => { setCustomRange(vals || []); setCurrentPage(1); }}
          />
        )}
        <Button onClick={resetFilters}>Reset</Button>
      </div>

      {/* Day-wise summary — always the last 7 days, regardless of the Date filter above */}
      {daySummaryQuery.isLoading ? (
        <div
          style={{
            margin: "0 8px 18px",
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "12px 14px",
          }}
        >
          <Skeleton active title={false} paragraph={{ rows: 1 }} />
        </div>
      ) : (
        daySummary.length > 0 && (
          <div
            style={{
              margin: "0 8px 18px",
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                Day-wise Summary
              </span>
              <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>
                Last 7 days · {activeTab === "all" ? "all records" : `${activeTab} records`}
              </span>
            </div>
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
              {daySummary.map((d) => (
                <div
                  key={d.date}
                  style={{
                    minWidth: 132,
                    flexShrink: 0,
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "10px 12px",
                    background: "#f8fafc",
                  }}
                >
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                    {new Date(d.date).toLocaleDateString("en-IN", {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                    })}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>
                    {d.count}
                  </div>
                  <div style={{ fontSize: 10.5, color: "#16a34a", marginTop: 2 }}>
                    {d.approved} approved
                    {d.pending ? (
                      <span style={{ color: "#f59e0b" }}> · {d.pending} pending</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      <div className="reports-table">
        <Table
          rowKey={(record) => record._id}
          dataSource={rows}
          pagination={false}
          onRow={(record) => ({
            onClick: () => setSelectedReport(record),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: isLoading ? (
              <div style={{ marginLeft: "20px", width: "95%" }}>
                <Skeleton title={false} active paragraph={{ rows: 6 }} />
              </div>
            ) : (
              <Empty />
            ),
          }}
        >
          <Column
            title="District"
            dataIndex="district"
            render={(t) => <span>{t || "—"}</span>}
          />
          <Column
            title="Constituency"
            dataIndex="constituency"
            render={(t) => <span>{t || "—"}</span>}
          />
          <Column
            title="Category"
            dataIndex="category"
            render={(t) => <span>{t || "—"}</span>}
          />
          <Column
            title="Message"
            dataIndex="message"
            render={(t) => (
              <div
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  maxWidth: 300,
                  lineHeight: "1.5em",
                  maxHeight: "4.5em",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {stripHtml(t) || "—"}
              </div>
            )}
          />
          <Column
            title="Status"
            dataIndex="status"
            render={(s) => <StatusTag status={s} />}
          />
          <Column
            title="Received"
            dataIndex="createdAt"
            render={(t) => (
              <span style={{ whiteSpace: "nowrap" }}>
                {t ? new Date(t).toLocaleString() : "—"}
              </span>
            )}
          />
          <Column
            title="Actions"
            render={(_, record) => renderRowActions(record)}
          />
        </Table>

        <div className="report-pagination">
          <div>
            <Select
              value={limit.toString()}
              style={{ width: 56 }}
              onChange={(value) => { setLimit(parseInt(value)); setCurrentPage(1); }}
              options={[
                { value: "10", label: "10" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
            <span className="px-4">Entries Per Page</span>
          </div>
          <div className="report-selection">
            <Pagination
              className="pagination"
              current={currentPage}
              pageSize={limit}
              total={totalPages * limit}
              onChange={(page, pageSize) => { setCurrentPage(page); setLimit(pageSize); }}
            />
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <ReportDetailModal
        record={selectedReport}
        activeTab={activeTab}
        onClose={() => setSelectedReport(null)}
        onStatusChange={updateStatus}
        updating={updating}
        categoryOptions={filterOptions.categories || []}
        onSaveEdit={saveReportEdit}
        saving={savingEdit}
      />

      {/* Bulk upload modal */}
      <Modal
        open={uploadOpen}
        title="Upload News Report"
        onCancel={() => {
          setUploadOpen(false);
          setUploadFile(null);
        }}
        onOk={handleBulkUpload}
        okText="Upload"
        confirmLoading={uploading}
        okButtonProps={{ disabled: !uploadFile }}
        destroyOnClose
      >
        <p style={{ marginBottom: 8, color: "#475569" }}>
          Upload an Excel sheet (.xlsx / .xls / .csv) with the columns:
        </p>
        <p style={{ marginBottom: 16, fontWeight: 600, color: "#0f172a" }}>
          District, Constituency, Category, Stringer Name, Date, Time, Message
        </p>
        <Upload.Dragger
          accept=".xlsx,.xls,.csv"
          maxCount={1}
          multiple={false}
          beforeUpload={(file) => {
            setUploadFile(file);
            return false; // prevent auto-upload; we send it manually
          }}
          onRemove={() => setUploadFile(null)}
          fileList={uploadFile ? [uploadFile] : []}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag an Excel file to this area</p>
          <p className="ant-upload-hint">Uploaded reports are marked as approved.</p>
        </Upload.Dragger>
      </Modal>
    </>
  );
};

export default NewsReportsList;
